import type { Dayjs } from 'dayjs';
import { isNotNullOrUndefined, isNullOrUndefined, vyDate, withDefault } from '@vy/utils';
import { PRICING_MODELS } from '@vy/types';
import { useFormContext } from 'react-hook-form';
import { VyDateRangePicker, VyStack } from '@vy/ui-design-system';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import { resetInstallmentsForStartDate } from './reset-installments';
import { getContractEndDate } from '../../../create-plan-page-services/get-contract-end-date';
import { ZERO_DURATION_MONTHS } from '../../../create-plan-page-consts/create-plan-default-values';

type OneTimeChargeDateRangePickerProps = {
  productIndex: number;
};

type PickerRange = [Dayjs | null, Dayjs | null];

export const OneTimeChargeDateRangePicker: React.FC<OneTimeChargeDateRangePickerProps> = ({ productIndex }) => {
  const { register, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const productStartDateRegister = register(`fixedProducts.${productIndex}.startDate`);
  const productEndDateRegister = register(`fixedProducts.${productIndex}.endDate`);

  const pricingModel = getValues(`fixedProducts.${productIndex}.variants.0.pricing.pricingModel`);
  const contractStartDate = getValues('startDate');
  const contractDurationInMonths = getValues('billingData.duration');
  const contractDurationOrZeroMonths = withDefault(contractDurationInMonths, ZERO_DURATION_MONTHS);
  const contractEndDate = getContractEndDate(
    vyDate(contractStartDate).toISOString(),
    contractDurationOrZeroMonths,
  );
  const amount = getValues(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.price`);
  const isCommercialTerms = pricingModel === PRICING_MODELS.CommercialTerms;

  const startDateValue = getValues(productStartDateRegister.name);
  const endDateValue = getValues(productEndDateRegister.name);

  const currentValue: PickerRange = [
    isNotNullOrUndefined(startDateValue) ? vyDate(startDateValue) : null,
    isNotNullOrUndefined(endDateValue) ? vyDate(endDateValue) : null,
  ];

  const handleDateRangeChange = (range: PickerRange) => {
    const [start, end] = range;
    const isSameDay = isNotNullOrUndefined(start) && isNotNullOrUndefined(end) && start.isSame(end, 'day');

    const startValue = isNotNullOrUndefined(start)
      ? vyDate(start.toDate(), { keepLocalTime: true }).startOf('day').toDate()
      : undefined;

    if (isSameDay || isNullOrUndefined(end)) {
      setValue(productStartDateRegister.name, startValue, { shouldDirty: true });
      setValue(productEndDateRegister.name, undefined, { shouldDirty: true });
    } else {
      const endOfDay = vyDate(end.toDate(), { keepLocalTime: true }).endOf('day').toDate();
      setValue(productStartDateRegister.name, startValue, { shouldDirty: true });
      setValue(productEndDateRegister.name, endOfDay, { shouldDirty: true });
    }

    if (isCommercialTerms && isNotNullOrUndefined(startValue)) {
      resetInstallmentsForStartDate({ productIndex, newStartDate: startValue, amount, getValues, setValue });
    }
  };

  return (
    <VyStack direction="column" data-field-span="full">
      <VyDateRangePicker
        label="Custom date"
        helperText="Choose a custom period to issue this charge"
        clearable
        value={currentValue}
        onChange={handleDateRangeChange}
        shouldDisableDate={(date: Dayjs) => {
          const currentDate = vyDate(date.toDate());
          const isBeforeContractStart = currentDate.isBefore(vyDate(contractStartDate), 'day');
          const isAfterContractEnd = currentDate.isAfter(contractEndDate, 'day');
          return isBeforeContractStart || isAfterContractEnd;
        }}
        data-vy="plan-charge-drawer-custom-date-range"
      />
    </VyStack>
  );
};
